<script lang="ts">
  import { get } from 'svelte/store'
  import {
    CRED_SCANNER_TARGET_ID,
    createScanner,
    getScannerErrorMessage,
    setupScanner,
    type ScannerLib,
  } from '@lib/scanner'
  import {SendTokens} from '@packages/ui'
  import { isValidSuiAddress } from '@mysten/sui/utils';

  import { clientPublicStore, fn, pageOptsStore, pageStore } from '@packages/stores'
  import { dispatch, once, PassportEvents } from '@lib/events/events'
  import { Pages } from '@packages/utils/enums'
  import { configStore } from '@src/stores/config'
  import { getBalance, getNativeSuiBalance, sendTokens } from '@src/passport/blockchain'
  import { getUserAddressBySub } from '@src/Passport.service'
  import { NOT_VALID_PASSPORT_QR_CODE_MESSAGE } from '@packages/utils/constants'

  const { toastAlert } = get(fn)

  let recipientAddress = ''
  let amount: number

  let activeCurrency = ''

  const symbol = 'CRED'
  let balance = ''

  const nativeSymbol = 'SUI'
  let nativeBalance = ''

  let isScannerHidden = true
  let isLoading = false

  let scanner: ScannerLib

  const onScannerClicked = async () => {
    const { scanner: scannerInstance, events } = await createScanner(CRED_SCANNER_TARGET_ID)
    scanner = scannerInstance
    once(PassportEvents.UI_CLOSED, scanner.close)
    
    setupScanner<{ sub: string }>(scanner, events, {
      onCapture: async ({ sub }) => {
        if (!sub) {
          toastAlert(NOT_VALID_PASSPORT_QR_CODE_MESSAGE(), 'failure')
          return scanner.close()
        }
        const { address } = await getUserAddressBySub(sub)
        recipientAddress = address
        scanner.close()
      },
      onCancel: () => (isScannerHidden = true),
      onError: (err) => {
        const message = getScannerErrorMessage(err)
        if (message?.includes('Permission'))
          alert('In order to scan please allow the current page to access your camera and try again.')
        isScannerHidden = true
      },
    })
    isScannerHidden = false
    await scanner.scan()
  }

  const sendTokensToRecipient = async () => {
    isLoading = true
    const isSuiNativeCoinTransfer = activeCurrency === nativeSymbol

    try {
      await sendTokens({ address: recipientAddress, amount, isSuiNativeCoinTransfer })

      toastAlert(`Send operation was completed.`)
      recipientAddress = ''
      amount = 0

      setTimeout(async () => {
        balance = await getBalance()
        nativeBalance = await getNativeSuiBalance()
        dispatch(PassportEvents.RECHECK_BALANCE)
      }, 1000)
    } catch (err) {
      console.log(err)
      dispatch(PassportEvents.ERROR, { error: err })
      toastAlert(err?.message || err || 'An error occurred while sending tokens', 'failure')
    }
    isLoading = false
  }

  const init = async () => {
    const pageOpts = get(pageOptsStore)
    recipientAddress = pageOpts?.to || ''
    amount = pageOpts?.amount || undefined
    balance = await getBalance()
    nativeBalance = parseFloat(await getNativeSuiBalance()).toFixed(4)
    activeCurrency = symbol
  }

  $: $configStore.suiNetwork && init()

  $: isRecipientAddressValid = isValidSuiAddress(recipientAddress)

  $: isSubmitDisabled =
    isLoading ||
    !amount ||
    !isRecipientAddressValid ||
    (activeCurrency === symbol && amount > +balance) ||
    (activeCurrency === nativeSymbol && amount > +nativeBalance)
</script>

<SendTokens
  bind:recipientAddress
  bind:amount
  options={{
    title: `Send stored value`,
    isScannerHidden,
    balance,
    isRecipientAddressValid,
    onScannerClicked,
    onSendTokens: sendTokensToRecipient,
    onBackClicked: () => pageStore.set(Pages.WALLET),
    onChangeCurrency: () => (activeCurrency === symbol ? (activeCurrency = nativeSymbol) : (activeCurrency = symbol)),
    onSetMaxAmount: () => (activeCurrency === symbol ? (amount = +balance) : (amount = +nativeBalance)),
    isLoading,
    isSubmitDisabled,
    credAlias: $clientPublicStore.ui_settings.cred_alias,
    activeCurrency,
    symbol,
    nativeSymbol,
    isNativeCurrecyTransferAllowed: $configStore.transferTokens?.nativeCurrency !== false,
    nativeBalance,
  }}
/>
